import React, {useState} from 'react'
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import ColorPicker from 'lib/ui/ColorPicker'
import TextEditor, {TextEditorContainer} from 'lib/ui/form/TextEditor'
import {Controller, useForm} from 'react-hook-form'
import {
  Townhall,
  useTownhallTemplate,
  useTownhallUpdate,
} from 'Event/template/Townhall'
import Typography from '@material-ui/core/Typography'
import styled from 'styled-components'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import Alert from '@material-ui/lab/Alert'
import AlertTitle from '@material-ui/lab/AlertTitle'
import ComponentConfig, {
  SaveButton,
} from 'organization/Event/DashboardConfig/ConfigDialog'
import {ComponentConfigProps} from 'organization/Event/DashboardConfig/ComponentConfigPanel'
import {Snackbar} from 'lib/ui/Snackbar'
import {useAddTranslation} from 'Event/LanguageProvider'
import {replaceAtPath} from 'lib/JsonUpdateProvider'
import {useGuestTranslations} from 'Event/LanguageProvider/translations'

export type LeaderboardConfigData = NonNullable<Townhall['leaderboard']>

export default function LeaderboardConfig(props: ComponentConfigProps) {
  const template = useTownhallTemplate()
  const {leaderboard, rewardAlert} = template
  const {register, control, handleSubmit} = useForm()
  const updateTownhall = useTownhallUpdate()
  const translate = useGuestTranslations()
  const addTranslation = useAddTranslation()

  const [rewardBackgroundColor, setRewardBackgroundColor] = useState<string>(
    rewardAlert.backgroundColor,
  )
  const [rewardTextColor, setRewardTextColor] = useState<string>(
    rewardAlert.textColor,
  )

  const submit = (
    data: LeaderboardConfigData & {points_unit: Townhall['points_unit']},
  ) => {
    const {points_unit, ...leaderboardData} = data

    const rewardText = replaceAtPath(
      data,
      'rewardAlert.text',
      '{{reward_text}}',
    )

    const backToDashboardText = replaceAtPath(
      data,
      'backToDashboardText',
      '{{leaderboard_back_to_dashboard_text}}',
    )

    const updated = {
      rewardAlert: {
        text: '{{reward_text}}',
        backgroundColor: rewardBackgroundColor,
        textColor: rewardTextColor,
      },
      points_unit: '{{points_unit}}',
      leaderboard: {
        ...leaderboardData,
        title: '{{leaderboard_title}}',
        description: '{{leaderboard_description}}',
        backToDashboardText: '{{leaderboard_back_to_dashboard_text}}',
      },
      localization: addTranslation({
        reward_text: rewardText ?? '',
        leaderboard_title: leaderboardData.title ?? '',
        leaderboard_description: leaderboardData.description ?? '',
        leaderboard_back_to_dashboard_text: backToDashboardText ?? '',
        points_unit: points_unit,
      }),
    }

    updateTownhall(updated)
    props.onClose()
  }

  return (
    <ComponentConfig
      isVisible={props.showing}
      onClose={props.onClose}
      title="Leaderboard"
    >
      <form onSubmit={handleSubmit(submit)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Alert severity="info">
              <AlertTitle>Variables</AlertTitle>
              <div>
                <Typography variant="caption">
                  {`{{points_unit}} - Name for points`}
                </Typography>
              </div>
              <div>
                <Typography variant="caption">
                  {`{{action_description}} - Action description`}
                </Typography>
              </div>
              <div>
                <Typography variant="caption">
                  {`{{action_points}} - Number of points received`}
                </Typography>
              </div>
              <div>
                <Typography variant="caption">
                  {`{{leaderboard_points}} - Attendee's current points`}
                </Typography>
              </div>
              <div>
                <Typography variant="caption">
                  {`{{leaderboard_position}} - Attendee's current position on leaderboard`}
                </Typography>
              </div>
            </Alert>
          </Grid>

          <Grid item xs={12}>
            <TextField
              name="points_unit"
              defaultValue={translate(template.points_unit)}
              label="Unit"
              fullWidth
              inputProps={{
                'aria-label': 'points unit',
                ref: register,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              defaultValue={translate(leaderboard.title)}
              name="title"
              label="Title"
              fullWidth
              inputProps={{
                'aria-label': 'set leaderboard page title',
                ref: register({required: 'Title is required'}),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="description"
              control={control}
              defaultValue={translate(leaderboard.description)}
              rules={{
                required: 'Description is required',
              }}
              render={({value, onChange}) => (
                <TextEditorContainer>
                  <TextEditor data={value} onChange={onChange} />
                </TextEditorContainer>
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="backToDashboardText"
              defaultValue={translate(leaderboard.backToDashboardText)}
              label="Back to Dashboard Text"
              fullWidth
              inputProps={{
                'aria-label': 'set leaderboard page back to dashboard text',
                ref: register({required: 'Back to dashboard text is required'}),
              }}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="backToDashboardTextColor"
              control={control}
              defaultValue={leaderboard.backToDashboardTextColor}
              render={({value, onChange}) => (
                <ColorPicker
                  label="Back to Dashboard Text Color"
                  color={value}
                  onPick={onChange}
                  aria-label="set back to dashboard text color"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <Snackbar
              message={
                <Continer>
                  <CheckCircleIcon />
                  <StyledDiv>{translate(rewardAlert.text)}</StyledDiv>
                </Continer>
              }
              color={rewardTextColor}
              backgroundColor={rewardBackgroundColor}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ColorPicker
              label="Popup Background Color"
              color={rewardBackgroundColor}
              onPick={setRewardBackgroundColor}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ColorPicker
              label="Popup Color"
              color={rewardTextColor}
              onPick={setRewardTextColor}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <TextField
              defaultValue={translate(rewardAlert.text)}
              name="rewardAlert.text"
              label="Popup Text"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              inputProps={{
                'aria-label': 'set leaderboard popup text',
                ref: register({required: 'Popup text is required'}),
                onKeyDown: (e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                  }
                },
              }}
            />
          </Grid>
        </Grid>
        <SaveButton>Save</SaveButton>
      </form>
    </ComponentConfig>
  )
}

const Continer = styled.div`
  display: flex;
`
const StyledDiv = styled.div`
  font-size: 19px;
`
